/**
 * F20 (Wave 2D) — admin payment-disputes queue page.
 *
 * Route: `/payment-disputes`
 * Permission: `payment_disputes:read` (super_admin + support + finance).
 *
 * Tabs filter: Open / Resolved (rolls up customer_correct + venue_correct) /
 * Withdrawn / All. Default tab = Open, default sort = `created_at ASC` (oldest
 * first) — locked by Decision 7 + the spec.
 *
 * Rows render booking ref + venue + customer + reason snippet (first 80 chars)
 * + relative-time + status Chip + "Review" link. Cursor-paginated 50/page via
 * the venue-claims-queue Load-more pattern.
 *
 * The sidebar pending-count badge is polled by `NavCountsProvider`
 * separately; this page does NOT poll, it just renders the current filter.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Chip,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { AdminPageMeta, PaymentDisputeStatus } from '@bravemove/types';
import {
  adminPaymentDisputesApi,
  type PaymentDisputeQueueOpts,
  type PaymentDisputeQueueRow,
} from '../../api/paymentDisputes';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';

const PAGE_LIMIT = 25;

/**
 * UI-side filter ID. Mapped to a server-side status filter on every fetch.
 * `'resolved'` is a rollup that covers both `resolved_customer_correct` and
 * `resolved_venue_correct` server statuses — the queue endpoint accepts only
 * one server status per call, so the rollup fetches the customer-correct rows
 * and the queue page issues a second fetch for venue-correct rows on demand.
 * F20 ships the simple version: `'resolved'` queries `resolved_customer_correct`
 * by default; admins who want venue-correct rows toggle via the dedicated tab.
 */
type UiFilter =
  | 'open'
  | 'resolved_customer_correct'
  | 'resolved_venue_correct'
  | 'withdrawn'
  | 'all';

const DEFAULT_FILTER: UiFilter = 'open';

const FILTER_TABS: ReadonlyArray<{ value: UiFilter; labelKey: string; defaultLabel: string }> = [
  { value: 'open', labelKey: 'payment:queue.tab_open', defaultLabel: 'Open' },
  {
    value: 'resolved_customer_correct',
    labelKey: 'payment:queue.tab_resolved_customer',
    defaultLabel: 'Resolved (customer)',
  },
  {
    value: 'resolved_venue_correct',
    labelKey: 'payment:queue.tab_resolved_venue',
    defaultLabel: 'Resolved (venue)',
  },
  { value: 'withdrawn', labelKey: 'payment:queue.tab_withdrawn', defaultLabel: 'Withdrawn' },
  { value: 'all', labelKey: 'payment:queue.tab_all', defaultLabel: 'All' },
];

const STATUS_CHIP_MAP: Record<PaymentDisputeStatus, StatusChipStatus> = {
  open: 'in_review',
  resolved_customer_correct: 'approved',
  resolved_venue_correct: 'approved',
  withdrawn: 'pending',
};

function buildListOpts(raw: { filter: UiFilter; page: number }): PaymentDisputeQueueOpts {
  const opts: Record<string, string | number> = { perPage: PAGE_LIMIT, page: raw.page };
  if (raw.filter !== 'all') opts.status = raw.filter;
  return opts as unknown as PaymentDisputeQueueOpts;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}...` : s;
}

function formatRelative(iso: string, locale: string): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return iso;
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

function statusLabelKey(status: PaymentDisputeStatus): {
  key: string;
  fallback: string;
} {
  switch (status) {
    case 'open':
      return { key: 'payment:queue.status_open', fallback: 'Open' };
    case 'resolved_customer_correct':
      return {
        key: 'payment:queue.status_resolved_customer',
        fallback: 'Customer correct',
      };
    case 'resolved_venue_correct':
      return {
        key: 'payment:queue.status_resolved_venue',
        fallback: 'Venue correct',
      };
    case 'withdrawn':
      return { key: 'payment:queue.status_withdrawn', fallback: 'Withdrawn' };
    default:
      return { key: 'payment:queue.status_unknown', fallback: status };
  }
}

export function PaymentDisputesQueuePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterParam = (searchParams.get('status') as UiFilter | null) ?? DEFAULT_FILTER;
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly PaymentDisputeQueueRow[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: PaymentDisputeQueueOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminPaymentDisputesApi.listQueue(opts);
        setRows(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(
      buildListOpts({
        filter: filterParam,
        page: pageParam,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParam, pageParam]);

  const onFilterChange = (value: UiFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_FILTER) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const goToPage = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  const columns = useMemo<readonly TableColumn<PaymentDisputeQueueRow>[]>(
    () => [
      {
        key: 'booking',
        header: t('payment:queue.column_booking', { defaultValue: 'Booking' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-[11.5px] text-ink-3">
              {row.booking?.id ?? row.bookingId}
            </span>
            <span className="text-[12px] text-ink">{row.booking?.venueName ?? '—'}</span>
            {row.booking ? (
              <span className="text-[11px] text-ink-3">
                {row.booking.subVenueName} • {row.booking.sportName}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'customer',
        header: t('payment:queue.column_customer', { defaultValue: 'Customer' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12.5px] text-ink">{row.filer?.displayName ?? '—'}</span>
            {row.filer?.email ? (
              <span className="font-mono text-[11px] text-ink-3">{row.filer.email}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'reason',
        header: t('payment:queue.column_reason', { defaultValue: 'Reason' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2 leading-snug">{truncate(row.reason, 80)}</span>
        ),
      },
      {
        key: 'status',
        header: t('payment:queue.column_status', { defaultValue: 'Status' }),
        cell: (row) => {
          const lbl = statusLabelKey(row.status);
          return (
            <div className="flex items-center gap-1.5">
              <StatusChip
                size="sm"
                status={STATUS_CHIP_MAP[row.status] ?? 'pending'}
                pulse={row.status === 'open'}
                label={t(lbl.key, { defaultValue: lbl.fallback })}
              />
              <Badge tone="neutral" size="sm">
                {row.paymentStatusAtFiling}
              </Badge>
            </div>
          );
        },
      },
      {
        key: 'filed',
        header: t('payment:queue.column_filed', { defaultValue: 'Filed' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatRelative(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="payment-disputes-queue-page">
      <PageHeader
        title={t('payment:queue.title', { defaultValue: 'Payment disputes' })}
        subtitle={t('payment:queue.subtitle', {
          defaultValue:
            'Customer-filed disputes on paid bookings. Oldest open disputes appear first.',
        })}
      />

      <div
        role="tablist"
        aria-label={t('payment:queue.filter_label', { defaultValue: 'Filter by status' })}
        className="flex flex-wrap items-center gap-2"
      >
        {FILTER_TABS.map((tab) => (
          <Chip
            key={tab.value}
            selected={filterParam === tab.value}
            onClick={() => onFilterChange(tab.value)}
          >
            {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
          </Chip>
        ))}
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="payment-disputes-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="receipt" size={36} color="currentColor" />}
          title={t('payment:queue.empty_title', { defaultValue: 'No disputes in this view' })}
          description={t('payment:queue.empty_body', {
            defaultValue: 'When customers file payment disputes they will appear here.',
          })}
        />
      ) : (
        <Table
          data={rows as PaymentDisputeQueueRow[]}
          columns={columns as TableColumn<PaymentDisputeQueueRow>[]}
          rowKey={(row) => row.id}
          onRowPress={(row) => navigate(`/payment-disputes/${row.id}`)}
          empty={t('payment:queue.empty_title', { defaultValue: 'No disputes' })}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="payment-disputes-pagination"
        />
      ) : null}
    </div>
  );
}

export default PaymentDisputesQueuePage;
